import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PlusIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import GlassModal from './Common/GlassModal';
import { supabase } from '../utils/supabaseClient';
import { useUser } from '../contexts/UserContext';
import { hasModuleAccess, MODULES } from '../utils/simplePermissions';
import { fmtTime, companyDateKey, companyTimeKey, companyWallClockToUtc, toCompanyDateKey, addDaysToDateKey, addMonthsToDateKey } from '../utils/dateFormatters';
import JobPartsService from '../services/JobPartsService';
import { showToast } from './Common/Toast';


/**
 * CompletionPromptModal
 *
 * Purpose: Smart prompt when job is completed
 * Shows 3 options:
 * 1. Create Invoice Now - Immediately generates invoice
 * 2. Mark Complete (Invoice Later) - Job stays in "Requires Invoicing"
 * 3. Extend Job - Opens ExtendJobModal to add more time
 *
 * Also handles: Equipment installation prompt for warranty tracking
 *
 * Part of: Phase 2 - Smart Status Progression
 */

const CompletionPromptModal = ({
  isOpen,
  onClose,
  onCreateInvoice,
  onMarkComplete,
  onExtendJob,
  jobTitle,
  customerId,
  workOrderId,
  onEquipmentAdded,
  // What was done on site. Prefills from the job, saved with the completion and printed in the
  // invoice's "Work Performed" section (operator ruling 2026-10-07: industry standard).
  initialWorkPerformed = '',
  // Field view passes false: the tech's completion is completion only; billing is the office's
  // job (operator ruling 2026-10-07). Offering "Create Invoice Now" there did nothing at all.
  allowInvoice = true
}) => {
  const { user } = useUser();
  // Whether THIS user may invoice. Gated on the same MODULES.INVOICES the Invoices page
  // and the sidebar use, so the completion prompt can no longer offer an action the
  // person cannot carry out: `invoices` is in FULL_TIER_ONLY_MODULES, applied last and
  // not overridable by a per-user toggle, so a Crew seat can never hold it.
  //
  // This is what Jobber and HouseCall Pro both do — the difference between them is only
  // WHETHER a given tech is granted invoicing, not whether the option is permission-
  // gated. Here that grant is a seat-tier decision the app already makes elsewhere; the
  // completion prompt simply predates it and offered "Create Invoice Now" to everyone.
  //
  // The tech path is not a new one: "Mark Complete (Invoice Later)" already moves the job
  // to Requires Invoicing for an owner/admin to bill, which is exactly the handoff.
  const canInvoice = allowInvoice && hasModuleAccess(user, MODULES.INVOICES);
  // Default to what this user can actually do — invoicing for full seats, plain
  // completion for crew. A default the user cannot act on is the same defect one step
  // earlier.
  const [selectedOption, setSelectedOption] = useState(canInvoice ? 'invoice' : 'complete'); // 'invoice', 'complete', 'extend'
  const [useCustomEndTime, setUseCustomEndTime] = useState(false);
  const [workPerformed, setWorkPerformed] = useState(initialWorkPerformed || '');
  // Refill from the job each time the prompt opens (the modal stays mounted between jobs).
  useEffect(() => {
    if (isOpen) setWorkPerformed(initialWorkPerformed || '');
  }, [isOpen, initialWorkPerformed]);

  // Parts used (operator ruling 2026-10-07): planned parts prefilled, editable, plus parts added
  // on site. Stock comes off ONCE, here, via JobPartsService.
  const [partsData, setPartsData] = useState(null);
  const [plannedParts, setPlannedParts] = useState([]);
  const [addedParts, setAddedParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [savingParts, setSavingParts] = useState(false);
  useEffect(() => {
    if (!isOpen || !workOrderId || !user?.company_id) {
      setPartsData(null);
      setPlannedParts([]);
      setAddedParts([]);
      return;
    }
    let cancelled = false;
    setPartsLoading(true);
    JobPartsService.load(workOrderId, user.company_id, user.id)
      .then((data) => {
        if (cancelled) return;
        setPartsData(data);
        setPlannedParts(data.planned);
        setAddedParts(data.extras || []);
      })
      .catch((e) => {
        console.error('Failed to load job parts:', e);
        if (!cancelled) setPartsData(null);
      })
      .finally(() => { if (!cancelled) setPartsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, workOrderId, user?.company_id, user?.id]);

  const updatePlanned = (i, patch) => setPlannedParts((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const updateAdded = (i, patch) => setAddedParts((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addPartRow = () => setAddedParts((rows) => [...rows, { itemId: '', qty: 1, locationId: partsData?.defaultLocationId || '', bill: true }]);
  const removeAddedRow = (i) => setAddedParts((rows) => rows.filter((_, idx) => idx !== i));
  const isTimeAndMaterials = partsData?.pricingModel === 'TIME_MATERIALS';
  const now = new Date();
  // Company-local wall clock, like every other job time on the page (fmtTime). The old pair
  // was the UTC day + the browser's clock: tomorrow's date at tonight's time, after 5pm Pacific.
  const [endDate, setEndDate] = useState(companyDateKey(now));
  const [endTime, setEndTime] = useState(companyTimeKey(now));

  // Equipment state
  const [showEquipmentSection, setShowEquipmentSection] = useState(false);
  const [existingEquipment, setExistingEquipment] = useState([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  
  // New equipment form
  const [showNewEquipmentForm, setShowNewEquipmentForm] = useState(false);
  const [newEquipment, setNewEquipment] = useState({
    equipment_type: '',
    manufacturer: '',
    model_number: '',
    serial_number: '',
    // warranty_days is the general form; warranty_months stays for the month/year
    // presets because those are calendar-accurate (setMonth handles varying month
    // lengths) while a day count must not be approximated. A 90-day warranty on a
    // used appliance is a real term and 3 months is NOT the same date — which is why
    // the presets alone could not express it.
    warranty_months: 12,
    warranty_days: null
  });
  const [savingEquipment, setSavingEquipment] = useState(false);

  // Load existing equipment when modal opens
  useEffect(() => {
    const loadEquipment = async () => {
      if (!isOpen || !customerId || !user?.company_id) {
        setExistingEquipment([]);
        return;
      }
      try {
        setEquipmentLoading(true);
        const { data, error } = await supabase
          .from('customer_equipment')
          .select('id, equipment_type, manufacturer, model_number, serial_number')
          .eq('customer_id', customerId)
          .eq('company_id', user.company_id)
          .order('created_at', { ascending: false });
        
        if (!error && data) {
          setExistingEquipment(data);
        }
      } catch (err) {
        console.error('Error loading equipment:', err);
      } finally {
        setEquipmentLoading(false);
      }
    };
    loadEquipment();
  }, [isOpen, customerId, user?.company_id]);

  // Reset equipment state when closing
  useEffect(() => {
    if (!isOpen) {
      setShowEquipmentSection(false);
      setSelectedEquipmentId('');
      setShowNewEquipmentForm(false);
      setNewEquipment({
        equipment_type: '',
        manufacturer: '',
        model_number: '',
        serial_number: '',
        warranty_months: 12
      });
    }
  }, [isOpen]);

  const getCompletionTime = () => {
    if (useCustomEndTime) {
      return companyWallClockToUtc(endDate, endTime);
    }
    return new Date().toISOString();
  };

  const handleSaveEquipment = async () => {
    if (!customerId || !user?.company_id) return null;
    
    try {
      setSavingEquipment(true);
      const completedAt = getCompletionTime();
      
      // Calculate warranty end date
      // The company's calendar day the job was completed on — not the UTC day of the instant.
      const warrantyStart = toCompanyDateKey(completedAt);
      // Days take precedence when set, and are added as DAYS rather than converted to
      // months — 90 days and 3 months land on different dates, and a warranty end date
      // is a contractual one.
      const warrantyEndDate = newEquipment.warranty_days
        ? addDaysToDateKey(warrantyStart, Number(newEquipment.warranty_days))
        : addMonthsToDateKey(warrantyStart, newEquipment.warranty_months || 12);

      // If linking existing equipment
      if (selectedEquipmentId) {
        // Update work_order with equipment link
        if (workOrderId) {
          await supabase
            .from('work_orders')
            .update({ customer_equipment_id: selectedEquipmentId })
            .eq('id', workOrderId);
        }
        onEquipmentAdded?.(selectedEquipmentId);
        return selectedEquipmentId;
      }

      // If creating new equipment
      if (newEquipment.equipment_type || newEquipment.manufacturer) {
        const { data, error } = await supabase
          .from('customer_equipment')
          .insert({
            company_id: user.company_id,
            customer_id: customerId,
            equipment_type: newEquipment.equipment_type || 'Unknown',
            manufacturer: newEquipment.manufacturer || 'Unknown',
            model_number: newEquipment.model_number || null,
            serial_number: newEquipment.serial_number || null,
            install_date: warrantyStart,
            warranty_start_date: warrantyStart,
            warranty_end_date: warrantyEndDate,
            status: 'active'
          })
          .select()
          .single();

        if (!error && data) {
          // Link equipment to work order
          if (workOrderId) {
            await supabase
              .from('work_orders')
              .update({ customer_equipment_id: data.id })
              .eq('id', workOrderId);
          }
          onEquipmentAdded?.(data.id);
          return data.id;
        }
      }
      return null;
    } catch (err) {
      console.error('Error saving equipment:', err);
      return null;
    } finally {
      setSavingEquipment(false);
    }
  };

  const handleConfirm = async () => {
    if (savingParts) return;
    setSavingParts(true);
    try {
      await confirmCompletion();
    } finally {
      setSavingParts(false);
    }
  };

  const confirmCompletion = async () => {
    // Parts input first: an emptied quantity box means "not answered", not 0 (on time & materials
    // 0 removes the part from the bill).
    if (selectedOption !== 'extend' && partsData) {
      const blank = [...plannedParts.map((p) => p.used), ...addedParts.map((a) => a.qty)]
        .some((v) => v === '' || v === null || v === undefined || !Number.isFinite(parseFloat(v)));
      if (blank) {
        showToast('error', 'Enter the quantity used for each part (0 if none).');
        return;
      }
    }

    // Save equipment first if selected
    if (showEquipmentSection && (selectedEquipmentId || newEquipment.equipment_type)) {
      await handleSaveEquipment();
    }

    // Parts BEFORE the completion callbacks: "Create Invoice Now" builds the invoice from the line
    // items, so parts added here must already be on the job. If recording fails, stay open.
    if (selectedOption !== 'extend' && partsData && (plannedParts.length || addedParts.length)) {
      const missingLocation = [...plannedParts.filter((p) => parseFloat(p.used) > 0), ...addedParts]
        .some((p) => !p.locationId);
      if (missingLocation) {
        showToast('error', 'Choose where each part was taken from.');
        return;
      }
      try {
        await JobPartsService.record({
          workOrderId,
          companyId: user.company_id,
          userId: user.id,
          planned: plannedParts,
          added: addedParts,
          pricingModel: partsData.pricingModel,
          items: partsData.items,
          addedPartsBillable: partsData.addedPartsBillable
        });
      } catch (e) {
        console.error('Failed to record parts:', e);
        showToast('error', e?.message || 'Could not record the parts used. The job was not completed.');
        return;
      }
    }

    const completedAt = getCompletionTime();
    const notes = workPerformed.trim();
    if (selectedOption === 'invoice') {
      onCreateInvoice(completedAt, notes);
    } else if (selectedOption === 'complete') {
      onMarkComplete(completedAt, notes);
    } else if (selectedOption === 'extend') {
      onExtendJob();
    }
    onClose();
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      closeOnOverlay={false}
      header={(
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/10 border border-white/10 flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold">Complete Job</h2>
          </div>
          <button type="button" aria-label="Close" title="Close" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </>
      )}
      footer={(
        <>
          <button onClick={onClose} className="px-4 py-2 font-medium text-white/80 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={savingParts || partsLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {selectedOption === 'invoice' && 'Create Invoice'}
            {selectedOption === 'complete' && 'Mark Complete'}
            {selectedOption === 'extend' && 'Extend Job'}
          </button>
        </>
      )}
    >
      <p className="mb-4 text-gray-300">
        Choose how to finish <span className="font-medium text-white">{jobTitle}</span>. Nothing changes until you confirm.
      </p>

      {/* Completion Time */}
      <div className="mb-6 rounded-xl p-4 bg-white/5 border border-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-300">Completion Time</span>
          <button
            type="button"
            onClick={() => setUseCustomEndTime(!useCustomEndTime)}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            {useCustomEndTime ? 'Use current time' : 'Set custom time'}
          </button>
        </div>
        {useCustomEndTime ? (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark]"
            />
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark]"
            />
          </div>
        ) : (
          <p className="text-sm text-white">Now — {fmtTime(now)}</p>
        )}
      </div>

      <div className="space-y-3">
        {canInvoice && (
        <label
          className={`flex items-start p-4 rounded-lg cursor-pointer transition-all border ${
            selectedOption === 'invoice'
              ? 'border-blue-400/60 bg-blue-500/10 backdrop-blur-sm'
              : 'border-white/10 hover:bg-white/5'
          }`}
        >
          <input
            type="radio"
            name="completion-option"
            value="invoice"
            checked={selectedOption === 'invoice'}
            onChange={(e) => setSelectedOption(e.target.value)}
            className="mt-1 mr-3"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <DocumentTextIcon className="w-5 h-5 text-blue-400" />
              <span className="font-semibold">Create Invoice Now</span>
            </div>
            <p className="text-sm opacity-80">
              Immediately generate an invoice for this job. You'll be taken to the invoice creation page.
            </p>
          </div>
        </label>
        )}

        <label
          className={`flex items-start p-4 rounded-lg cursor-pointer transition-all border ${
            selectedOption === 'complete'
              ? 'border-blue-400/60 bg-blue-500/10 backdrop-blur-sm'
              : 'border-white/10 hover:bg-white/5'
          }`}
        >
          <input
            type="radio"
            name="completion-option"
            value="complete"
            checked={selectedOption === 'complete'}
            onChange={(e) => setSelectedOption(e.target.value)}
            className="mt-1 mr-3"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircleIcon className="w-5 h-5 text-green-400" />
              <span className="font-semibold">Mark Complete (Invoice Later)</span>
            </div>
            <p className="text-sm opacity-80">
              Job will be marked complete and moved to "Requires Invoicing" status. You can invoice it later.
            </p>
          </div>
        </label>

        <label
          className={`flex items-start p-4 rounded-lg cursor-pointer transition-all border ${
            selectedOption === 'extend'
              ? 'border-blue-400/60 bg-blue-500/10 backdrop-blur-sm'
              : 'border-white/10 hover:bg-white/5'
          }`}
        >
          <input
            type="radio"
            name="completion-option"
            value="extend"
            checked={selectedOption === 'extend'}
            onChange={(e) => setSelectedOption(e.target.value)}
            className="mt-1 mr-3"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <ClockIcon className="w-5 h-5 text-orange-400" />
              <span className="font-semibold">Extend Job</span>
            </div>
            <p className="text-sm opacity-80">
              Job needs more time? Add additional hours to continue working on this job.
            </p>
          </div>
        </label>
      </div>

      {selectedOption !== 'extend' && (
        <div className="mt-4">
          <label htmlFor="completion-work-performed" className="block text-sm font-medium mb-1">
            Work performed <span className="opacity-60 font-normal">(optional — shows on the invoice)</span>
          </label>
          <textarea
            id="completion-work-performed"
            value={workPerformed}
            onChange={(e) => setWorkPerformed(e.target.value)}
            rows={3}
            placeholder="e.g. Replaced failed capacitor, tested system, cooling restored"
            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      )}

      {selectedOption !== 'extend' && workOrderId && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Parts used</span>
            {partsData && (
              <button type="button" onClick={addPartRow} className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300">
                <PlusIcon className="w-4 h-4" /> Add a part
              </button>
            )}
          </div>
          {partsLoading && <p className="text-sm opacity-70">Loading parts…</p>}
          {!partsLoading && !partsData && (
            <p className="text-sm opacity-70">Parts could not be loaded. You can still complete the job.</p>
          )}
          {partsData && plannedParts.length === 0 && addedParts.length === 0 && (
            <p className="text-sm opacity-70">No inventory parts on this job. Add any you used from stock.</p>
          )}
          {partsData && plannedParts.length > 0 && (
            <div className="space-y-2">
              {plannedParts.map((p, i) => (
                <div key={p.lineItemId} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-5 truncate" title={p.name}>
                    {p.name}
                    <span className="block text-xs opacity-60">Planned {p.plannedQty}</span>
                  </div>
                  <input
                    type="number" min="0" step="any" aria-label={`Quantity of ${p.name} used`}
                    value={p.used}
                    onChange={(e) => updatePlanned(i, { used: e.target.value })}
                    className="col-span-2 px-2 py-1 rounded bg-white/10 border border-white/20"
                  />
                  <select
                    aria-label={`Where ${p.name} was taken from`}
                    value={p.locationId}
                    onChange={(e) => updatePlanned(i, { locationId: e.target.value })}
                    className="col-span-5 px-2 py-1 rounded bg-white/10 border border-white/20"
                  >
                    <option value="">Taken from…</option>
                    {partsData.locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({partsData.stock.get(`${p.itemId}|${l.id}`) || 0} on hand)
                      </option>
                    ))}
                  </select>
                </div>
              ))}
              <p className="text-xs opacity-60">
                Unused parts stay in stock.{' '}
                {isTimeAndMaterials
                  ? 'This is a time & materials job: the invoice bills the quantity used.'
                  : partsData.addedPartsBillable
                    ? 'The invoice keeps the quoted quantities; added parts are billed if "Bill" is ticked.'
                    : 'This job is billed at its quoted price, so parts only update stock.'}
              </p>
            </div>
          )}
          {addedParts.length > 0 && (
            <div className="space-y-2 mt-2">
              {addedParts.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <select
                    aria-label="Part added"
                    value={a.itemId}
                    onChange={(e) => updateAdded(i, { itemId: e.target.value })}
                    className="col-span-4 px-2 py-1 rounded bg-white/10 border border-white/20"
                  >
                    <option value="">Choose a part…</option>
                    {partsData.items.map((it) => (
                      <option key={it.id} value={it.id}>{it.name}{it.sku ? ` (${it.sku})` : ''}</option>
                    ))}
                  </select>
                  <input
                    type="number" min="0" step="any" aria-label="Quantity added"
                    value={a.qty}
                    onChange={(e) => updateAdded(i, { qty: e.target.value })}
                    className="col-span-2 px-2 py-1 rounded bg-white/10 border border-white/20"
                  />
                  <select
                    aria-label="Where the added part was taken from"
                    value={a.locationId}
                    onChange={(e) => updateAdded(i, { locationId: e.target.value })}
                    className="col-span-3 px-2 py-1 rounded bg-white/10 border border-white/20"
                  >
                    <option value="">Taken from…</option>
                    {partsData.locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({a.itemId ? partsData.stock.get(`${a.itemId}|${l.id}`) || 0 : '–'})
                      </option>
                    ))}
                  </select>
                  {partsData.addedPartsBillable ? (
                    <label className="col-span-2 flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={a.bill} onChange={(e) => updateAdded(i, { bill: e.target.checked })} />
                      Bill
                    </label>
                  ) : (
                    <span className="col-span-2 text-xs opacity-60">{a.alreadyPosted ? 'Taken earlier' : 'Stock only'}</span>
                  )}
                  <button type="button" onClick={() => removeAddedRow(i)} aria-label="Remove added part" className="col-span-1 opacity-70 hover:opacity-100">
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Equipment Installation Section - Warranty Tracking */}
      <div className="mt-6 pt-4 border-t border-white/10">
        {!showEquipmentSection ? (
          <button
            type="button"
            onClick={() => setShowEquipmentSection(true)}
            className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
          >
            <WrenchScrewdriverIcon className="w-4 h-4" />
            Add equipment installed on this job
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">Equipment Installed</span>
              <button
                type="button"
                onClick={() => setShowEquipmentSection(false)}
                className="text-xs text-gray-500 hover:text-gray-400"
              >
                Remove
              </button>
            </div>

            {/* Link existing or create new */}
            {!showNewEquipmentForm ? (
              <div className="space-y-2">
                {equipmentLoading ? (
                  <p className="text-sm text-gray-500">Loading equipment...</p>
                ) : existingEquipment.length > 0 ? (
                  <>
                    <select
                      value={selectedEquipmentId}
                      onChange={(e) => setSelectedEquipmentId(e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select existing equipment</option>
                      {existingEquipment.map(eq => (
                        <option key={eq.id} value={eq.id}>
                          {eq.equipment_type} - {eq.manufacturer} {eq.model_number}
                          {eq.serial_number ? ` (S/N: ${eq.serial_number})` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewEquipmentForm(true)}
                      className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300"
                    >
                      <PlusIcon className="w-3 h-3" /> Add new equipment
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowNewEquipmentForm(true)}
                    className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300"
                  >
                    <PlusIcon className="w-4 h-4" /> Add new equipment
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2 p-3 bg-white/5 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">New equipment will have warranty start date set to completion time</p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="Equipment type *"
                    value={newEquipment.equipment_type}
                    onChange={(e) => setNewEquipment({...newEquipment, equipment_type: e.target.value})}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Manufacturer *"
                    value={newEquipment.manufacturer}
                    onChange={(e) => setNewEquipment({...newEquipment, manufacturer: e.target.value})}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Model number"
                    value={newEquipment.model_number}
                    onChange={(e) => setNewEquipment({...newEquipment, model_number: e.target.value})}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Serial number"
                    value={newEquipment.serial_number}
                    onChange={(e) => setNewEquipment({...newEquipment, serial_number: e.target.value})}
                    className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400">Warranty:</label>
                  <select
                    value={newEquipment.warranty_days ? 'custom' : String(newEquipment.warranty_months)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setNewEquipment(v === 'custom'
                        // 90 is the starting value because it is the term this option
                        // exists for — used appliances and short parts warranties.
                        ? { ...newEquipment, warranty_days: 90, warranty_months: null }
                        : { ...newEquipment, warranty_months: parseInt(v, 10), warranty_days: null });
                    }}
                    className="px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={6}>6 months</option>
                    <option value={12}>1 year</option>
                    <option value={24}>2 years</option>
                    <option value={36}>3 years</option>
                    <option value={60}>5 years</option>
                    <option value="custom">Custom (days)…</option>
                  </select>
                  {newEquipment.warranty_days != null && (
                    <>
                      <input
                        type="number"
                        min={1}
                        max={3650}
                        value={newEquipment.warranty_days}
                        onChange={(e) => setNewEquipment({
                          ...newEquipment,
                          warranty_days: e.target.value === '' ? '' : Math.max(1, Math.min(3650, parseInt(e.target.value, 10) || 0))
                        })}
                        aria-label="Warranty length in days"
                        className="w-20 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-xs focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-xs text-gray-400">days</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setShowNewEquipmentForm(false)}
                  className="text-xs text-gray-500 hover:text-gray-400"
                >
                  ← Link existing equipment instead
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </GlassModal>
  );
};

export default CompletionPromptModal;

